import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Badge, { BadgeType } from 'src/components/ui/Badge/Badge';
import Button, { ButtonToneProps } from 'src/components/ui/Button/Button';
import Modal from 'src/components/layouts/Modal/Modal';
import Textarea from 'src/components/ui/Textarea/Textarea';
import ConfirmationModal from 'src/components/modals/ConfirmationModal/ConfirmationModal';
import {
  Commitment,
  CommitmentStatus,
  COMMITMENT_STATUS_LABEL_KEY,
  useCommitmentMutations,
  useCommitmentsQuery,
} from 'src/services/commitment';
import { CommitmentDetailOverlay } from './CommitmentOverlay';

const MIN_DECLINE_REASON = 5;

const STATUS_BADGE: Record<string, BadgeType> = {
  pending_customer: BadgeType.Warning,
  active: BadgeType.Successful,
  rejected_customer: BadgeType.Error,
  terminated: BadgeType.Neutral,
  superseded: BadgeType.Neutral,
};

const fmtDate = (value?: string | null) => {
  if (!value) return '–';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleDateString('de-AT');
};

export default function AgreementsOverview() {
  const { t } = useTranslation();
  const { data: commitments } = useCommitmentsQuery();
  const { approveMutation, declineMutation } = useCommitmentMutations();

  const [detailId, setDetailId] = useState<number | null>(null);
  const [approveTarget, setApproveTarget] = useState<Commitment | null>(null);
  const [declineTarget, setDeclineTarget] = useState<Commitment | null>(null);
  const [declineReason, setDeclineReason] = useState('');
  const [declineError, setDeclineError] = useState<string | null>(null);
  const busy = approveMutation.isPending || declineMutation.isPending;

  const label = (key: string, fallback: string) => t(key, { defaultValue: fallback });
  const statusLabel = (status: string) =>
    t(COMMITMENT_STATUS_LABEL_KEY[status as CommitmentStatus] ?? `commitments.status_${status}`, {
      defaultValue: status,
    });
  const categoryLabel = (cat?: string | null) => (cat ? t(`commitments.category.${cat}`, { defaultValue: cat }) : '–');

  const rows = (commitments as Commitment[] | undefined) ?? [];

  async function onApproveConfirmed(confirmed: boolean) {
    const target = approveTarget;
    setApproveTarget(null);
    if (!confirmed || !target) return;
    try {
      await approveMutation.mutateAsync({ params: { path: { commitment_id: target.id } } });
      toast.success(label('commitments.approve_success', 'Commitment approved.'));
    } catch (e) {
      toast.error(label('commitments.action_failed', 'Could not save your decision.'));
    }
  }

  function openDecline(c: Commitment) {
    setDeclineTarget(c);
    setDeclineReason('');
    setDeclineError(null);
  }

  async function submitDecline() {
    if (!declineTarget) return;
    if (declineReason.trim().length < MIN_DECLINE_REASON) {
      setDeclineError(label('commitments.decline_reason_min', 'Please give a short reason (min. 5 characters).'));
      return;
    }
    try {
      await declineMutation.mutateAsync({
        params: { path: { commitment_id: declineTarget.id } },
        body: { reason: declineReason.trim() },
      });
      toast.success(label('commitments.decline_success', 'Commitment declined.'));
      setDeclineTarget(null);
    } catch (e) {
      toast.error(label('commitments.action_failed', 'Could not save your decision.'));
    }
  }

  return (
    <div className="flex flex-col gap-16 pt-24">
      <div className="surface-elevated border border-subtle rounded-base overflow-hidden">
        <table className="w-full text-left">
          <thead>
            <tr className="border-subtle-bottom">
              <th className="px-16 py-12 typo-text-12 text-faded">{label('commitments.col_number', 'No.')}</th>
              <th className="px-16 py-12 typo-text-12 text-faded">{label('commitments.col_title', 'Title')}</th>
              <th className="px-16 py-12 typo-text-12 text-faded">{label('commitments.col_category', 'Category')}</th>
              <th className="px-16 py-12 typo-text-12 text-faded">
                {label('commitments.col_date_from', 'Valid from')}
              </th>
              <th className="px-16 py-12 typo-text-12 text-faded">{label('commitments.col_date_to', 'Valid until')}</th>
              <th className="px-16 py-12 typo-text-12 text-faded">{label('commitments.col_status', 'Status')}</th>
              <th className="px-16 py-12" aria-label={label('commitments.col_actions', 'Actions')} />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={7} className="px-16 py-24 typo-text-14 text-faded text-center">
                  {label('commitments.empty', 'No agreements yet.')}
                </td>
              </tr>
            )}
            {rows.map(c => (
              <tr
                key={c.id}
                role="button"
                onClick={() => setDetailId(c.id)}
                className="border-subtle-bottom hover:surface-base cursor-pointer">
                <td className="px-16 py-12 typo-text-14--bold text-full">{c.name}</td>
                <td className="px-16 py-12 typo-text-14 text-full">{c.title || '–'}</td>
                <td className="px-16 py-12 typo-text-14 text-faded">{categoryLabel(c.category)}</td>
                <td className="px-16 py-12 typo-text-14 text-faded">{fmtDate(c.date_from)}</td>
                <td className="px-16 py-12 typo-text-14 text-faded">{fmtDate(c.date_to)}</td>
                <td className="px-16 py-12">
                  <Badge
                    className="max-w-min"
                    label={statusLabel(c.status)}
                    expanded
                    type={STATUS_BADGE[c.status] ?? BadgeType.Neutral}
                  />
                </td>
                <td className="px-16 py-12">
                  {c.status === 'pending_customer' && (
                    <div className="flex items-center justify-end gap-8" onClick={e => e.stopPropagation()} role="none">
                      <Button
                        tone={ButtonToneProps.SUCCESS}
                        icon="checkmark"
                        title={label('commitments.approve', 'Approve')}
                        disabled={busy}
                        onClick={() => setApproveTarget(c)}
                      />
                      <Button
                        tone={ButtonToneProps.DESTRUCTIVE}
                        icon="close"
                        title={label('commitments.decline', 'Decline')}
                        disabled={busy}
                        onClick={() => openDecline(c)}
                      />
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ConfirmationModal
        isOpen={!!approveTarget}
        onClose={confirmed => onApproveConfirmed(confirmed)}
        title={label('commitments.approve_confirm_title', 'Approve agreement?')}
        description={label(
          'commitments.approve_confirm_text',
          'By approving you confirm this agreement. This decision is sent to WeShip.',
        )}
      />

      {declineTarget && (
        <Modal isOpen onClose={() => setDeclineTarget(null)}>
          <div className="max-w-[440px] w-full p-32 flex flex-col gap-16">
            <h4 className="typo-title-24 text-full">{label('commitments.decline_title', 'Decline agreement')}</h4>
            <p className="typo-text-14 text-faded">
              {label('commitments.decline_text', 'Please tell us briefly why you decline this agreement.')}
            </p>
            <Textarea
              value={declineReason}
              onChange={e => setDeclineReason(e.target.value)}
              placeholder={label('commitments.decline_reason_placeholder', 'Reason…')}
              minRows={3}
            />
            {declineError && <span className="typo-text-12 status-error-full">{declineError}</span>}
            <div className="flex justify-end gap-12">
              <Button
                tone={ButtonToneProps.SECONDARY}
                title={t('general.cancel')}
                onClick={() => setDeclineTarget(null)}
                disabled={busy}
              />
              <Button
                tone={ButtonToneProps.DESTRUCTIVE}
                title={label('commitments.decline', 'Decline')}
                onClick={() => submitDecline()}
                disabled={busy}
              />
            </div>
          </div>
        </Modal>
      )}

      {detailId != null && <CommitmentDetailOverlay id={detailId} onClose={() => setDetailId(null)} />}
    </div>
  );
}
